// Generated by GitHub Copilot
import { Api } from '#core/api/Api.ts'
import GuestIdentity from '#core/entities/auth/guests/GuestIdentity.ts'

/**
 * API client for resolving and anonymizing guest identities. Requires AUTH_USERS
 * permissions. Callers decide an id is a guest by decoding its UUID bits
 * (see {@link getUserType}) and only call this for GUEST-typed ids.
 */
export default class GuestIdentitiesApi extends Api<GuestIdentitiesApi> {
  private static readonly basePath = 'auth/guest-identities'

  /**
   * Resolves guest identities by id for the current company (for display).
   * Uses repeated `ids` query params (`?ids=x&ids=y`) as required by FastAPI.
   */
  public async resolve(ids: string[]): Promise<GuestIdentity[]> {
    if (ids.length === 0) return []
    const params = new URLSearchParams()
    for (const id of ids) params.append('ids', id)
    return (await this.get<GuestIdentity[]>(`${GuestIdentitiesApi.basePath}/?${params.toString()}`, undefined, GuestIdentity)) ?? []
  }

  /**
   * Lists every guest submitter in the current company, optionally filtered by a
   * search string. The backend endpoint has no text search, so filtering is applied
   * locally. Guests are a separate source from company users; a picker offering both
   * fetches this list and the user list and merges them.
   * @param search - Optional search term matched against display name and email.
   */
  public async listGuests(search?: string): Promise<GuestIdentity[]> {
    const guests = (await this.get<GuestIdentity[]>(`${GuestIdentitiesApi.basePath}/`, undefined, GuestIdentity)) ?? []
    if (!search?.trim()) return guests
    const q = search.trim().toLocaleLowerCase()
    return guests.filter((g) =>
      (g.display_name?.toLocaleLowerCase().includes(q) ?? false) ||
      (g.email?.toLocaleLowerCase().includes(q) ?? false),
    )
  }

  /**
   * Anonymizes a guest identity (GDPR erasure), keeping its id so references still resolve.
   * @throws {ApiError} 400 when the id is not a guest identity, 404 when not found.
   */
  public async anonymize(guestId: string): Promise<GuestIdentity | null> {
    return this.post<GuestIdentity>(`${GuestIdentitiesApi.basePath}/${guestId}/anonymize`, undefined, GuestIdentity)
  }
}
